import { createTheme } from "@mui/material/styles";
import {
  GLOBAL_BUTTON_HEIGHT,
  GLOBAL_BUTTON_ICON_SIZE,
  PILL_BUTTON_BG,
  PILL_BUTTON_DISABLED_BG,
  PILL_BUTTON_DISABLED_BORDER,
  PILL_BUTTON_DISABLED_TEXT,
} from "./utils/pillButtonStyles";
import {
  defaultCheckboxCheckedIcon,
  defaultCheckboxIcon,
  defaultCheckboxIndeterminateIcon,
} from "./utils/checkboxIcons";

const PURPLE = "#9C4FFF";
const PURPLE_DARK = "#7C3AED";
const SOFT_PURPLE_HOVER_BG = "#F5F3FF";

const tableOutlinedPillSelector = [
  '.MuiTable-root [data-column-id="status"] .MuiChip-root',
  '.MuiTable-root [data-column-id$="_status"] .MuiChip-root',
  '.MuiTable-root [data-column-label~="Status" i] .MuiChip-root',
  '.MuiTable-root .MuiChip-root[data-table-status]',
].join(", ");

const globalButtonInteractionSx = {
  background: "transparent !important",
  backgroundColor: "transparent !important",
  backgroundImage: "none !important",
  color: `${PILL_BUTTON_BG} !important`,
  border: "none !important",
  borderColor: "transparent !important",
  boxShadow: "none !important",
};

const theme = createTheme({
  palette: {
    primary: {
      main: PILL_BUTTON_BG,
      dark: PURPLE_DARK,
      contrastText: "#FFFFFF",
    },
  },
  typography: {
    fontFamily: '"Be Vietnam Pro", sans-serif',
    body1: { fontSize: 14, fontWeight: 400, lineHeight: "24px" },
    body2: { fontSize: 14, fontWeight: 400, lineHeight: "24px" },
    subtitle1: { fontSize: 16, fontWeight: 700, lineHeight: "24px" },
    subtitle2: { fontSize: 14, fontWeight: 500, lineHeight: 1 },
    h6: { fontSize: 20, fontWeight: 700, lineHeight: "30px" },
    caption: { fontSize: 12, fontWeight: 400, lineHeight: "20px" },
    button: { fontSize: 12, fontWeight: 400, lineHeight: "20px" },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Keep header text consistent across shared and page-specific tables.
        "th, thead td, .MuiTableHead-root .MuiTableCell-root, .MuiTableCell-head, [role='columnheader']": {
          color: "#344054",
          fontSize: "12px !important",
          whiteSpace: "nowrap !important",
          // Header labels may be wrapped in Typography, links, or custom elements.
          "& :not(svg):not(svg *)": {
            fontSize: "12px !important",
            whiteSpace: "nowrap !important",
          },
        },
        ".MuiButton-root": {
          transition:
            "background-color 140ms ease, background 140ms ease, color 140ms ease, border-color 140ms ease, box-shadow 140ms ease",
        },
        // Platform default for solid actions: purple pill at rest, outlined
        // purple on hover. Focused/visited actions keep white text. Keep error actions free to retain their
        // destructive red treatment.
        ".MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError)": {
          borderRadius: "999px !important",
          color: "#FFFFFF !important",
          background: `${PILL_BUTTON_BG} !important`,
          backgroundColor: `${PILL_BUTTON_BG} !important`,
          border: `1px solid ${PILL_BUTTON_BG} !important`,
          boxShadow: "none !important",
          fontWeight: "400 !important",
        },
        ".MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError):hover": {
          color: `${PILL_BUTTON_BG} !important`,
          background: "#FFFFFF !important",
          backgroundColor: "#FFFFFF !important",
          border: `1px solid ${PILL_BUTTON_BG} !important`,
          borderColor: `${PILL_BUTTON_BG} !important`,
          boxShadow: "none !important",
        },
        ".MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError):hover .MuiButton-startIcon, .MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError):hover .MuiButton-endIcon, .MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError):focus-visible .MuiButton-startIcon, .MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError):focus-visible .MuiButton-endIcon": {
          color: `${PILL_BUTTON_BG} !important`,
        },
        ".MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError):hover .MuiButton-startIcon svg, .MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError):hover .MuiButton-endIcon svg, .MuiButton-root.pill:hover:not(.Mui-disabled) .MuiButton-startIcon svg, .MuiButton-root.pill:hover:not(.Mui-disabled) .MuiButton-endIcon svg": {
          color: `${PILL_BUTTON_BG} !important`,
          stroke: `${PILL_BUTTON_BG} !important`,
          fill: "none !important",
        },
        ".MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError):hover .MuiButton-startIcon svg [stroke]:not([stroke='none']), .MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError):hover .MuiButton-endIcon svg [stroke]:not([stroke='none']), .MuiButton-root.pill:hover:not(.Mui-disabled) .MuiButton-startIcon svg [stroke]:not([stroke='none']), .MuiButton-root.pill:hover:not(.Mui-disabled) .MuiButton-endIcon svg [stroke]:not([stroke='none'])": {
          stroke: `${PILL_BUTTON_BG} !important`,
        },
        ".MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError):hover .MuiButton-startIcon svg [fill]:not([fill='none']), .MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError):hover .MuiButton-endIcon svg [fill]:not([fill='none']), .MuiButton-root.pill:hover:not(.Mui-disabled) .MuiButton-startIcon svg [fill]:not([fill='none']), .MuiButton-root.pill:hover:not(.Mui-disabled) .MuiButton-endIcon svg [fill]:not([fill='none'])": {
          fill: "none !important",
          stroke: `${PILL_BUTTON_BG} !important`,
          strokeWidth: "1.25px !important",
          strokeLinecap: "round !important",
          strokeLinejoin: "round !important",
        },
        ".MuiButton-root.pill": {
          fontWeight: "400 !important",
        },
        ".MuiButton-root.pill:hover:not(.Mui-disabled)": {
          background: "#FFFFFF !important",
          backgroundColor: "#FFFFFF !important",
          backgroundImage: "none !important",
          color: `${PILL_BUTTON_BG} !important`,
          border: `1px solid ${PILL_BUTTON_BG} !important`,
          borderColor: `${PILL_BUTTON_BG} !important`,
          boxShadow: "none !important",
        },
        ".MuiButton-root.pill:hover:not(.Mui-disabled) .MuiButton-startIcon, .MuiButton-root.pill:hover:not(.Mui-disabled) .MuiButton-endIcon, .MuiButton-root.pill:focus-visible:not(.Mui-disabled) .MuiButton-startIcon, .MuiButton-root.pill:focus-visible:not(.Mui-disabled) .MuiButton-endIcon, .MuiButton-root.pill.Mui-focusVisible:not(.Mui-disabled) .MuiButton-startIcon, .MuiButton-root.pill.Mui-focusVisible:not(.Mui-disabled) .MuiButton-endIcon": {
          color: `${PILL_BUTTON_BG} !important`,
        },
        ".MuiButton-root.pill:hover:not(.Mui-disabled) .MuiButton-startIcon svg, .MuiButton-root.pill:hover:not(.Mui-disabled) .MuiButton-endIcon svg, .MuiButton-root.pill:focus-visible:not(.Mui-disabled) .MuiButton-startIcon svg, .MuiButton-root.pill:focus-visible:not(.Mui-disabled) .MuiButton-endIcon svg, .MuiButton-root.pill.Mui-focusVisible:not(.Mui-disabled) .MuiButton-startIcon svg, .MuiButton-root.pill.Mui-focusVisible:not(.Mui-disabled) .MuiButton-endIcon svg": {
          color: `${PILL_BUTTON_BG} !important`,
          fill: "currentColor !important",
        },
        ".MuiButton-root.pill.pill-danger:hover:not(.Mui-disabled), .MuiButton-root.pill.pill-danger:focus:not(.Mui-disabled), .MuiButton-root.pill.pill-danger:focus-visible:not(.Mui-disabled), .MuiButton-root.pill.pill-danger.Mui-focusVisible:not(.Mui-disabled), .MuiButton-root.pill.pill-danger:active:not(.Mui-disabled)": {
          color: "#EF4444 !important",
          borderColor: "#EF4444 !important",
        },
        ".MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError):is(:focus, :focus-visible, .Mui-focusVisible, :visited)": {
          color: "#FFFFFF !important",
          background: `${PILL_BUTTON_BG} !important`,
          backgroundColor: `${PILL_BUTTON_BG} !important`,
          "& .MuiButton-startIcon, & .MuiButton-endIcon, & svg": { color: "#FFFFFF !important" },
          "& svg [stroke]:not([stroke='none'])": { stroke: "currentColor !important" },
        },
        ".MuiButton-root.pill.Mui-disabled": {
          background: `${PILL_BUTTON_DISABLED_BG} !important`,
          backgroundColor: `${PILL_BUTTON_DISABLED_BG} !important`,
          backgroundImage: "none !important",
          color: `${PILL_BUTTON_DISABLED_TEXT} !important`,
          borderColor: `${PILL_BUTTON_DISABLED_BORDER} !important`,
          boxShadow: "none !important",
          opacity: "1 !important",
        },
        ".MuiButton-root .MuiButton-startIcon, .MuiButton-root .MuiButton-endIcon": {
          color: "inherit !important",
        },
        ".MuiButton-root .MuiButton-startIcon svg, .MuiButton-root .MuiButton-endIcon svg": {
          color: "inherit !important",
        },
        ".MuiButton-root .MuiButton-startIcon svg [stroke]:not([stroke='none']), .MuiButton-root .MuiButton-endIcon svg [stroke]:not([stroke='none'])": {
          stroke: "currentColor !important",
        },
        ".MuiButton-root .MuiButton-startIcon svg [fill]:not([fill='none']), .MuiButton-root .MuiButton-endIcon svg [fill]:not([fill='none'])": {
          fill: "currentColor !important",
        },
        ".MuiButton-root:hover:not(.Mui-disabled):not(.pill):not(.MuiButton-contained):not(.rpo-login-submit), .MuiButton-root.Mui-focusVisible:not(.Mui-disabled):not(.pill):not(.MuiButton-contained):not(.rpo-login-submit)": {
          ...globalButtonInteractionSx,
        },
        ".MuiButton-root:active:not(.Mui-disabled):not(.pill):not(.MuiButton-contained):not(.rpo-login-submit)": {
          ...globalButtonInteractionSx,
        },
        ".MuiButton-root.bulk-action-flat": {
          background: "transparent !important",
          backgroundColor: "transparent !important",
          backgroundImage: "none !important",
          color: `${PURPLE} !important`,
          border: "none !important",
          borderColor: "transparent !important",
          boxShadow: "none !important",
          outline: "none !important",
        },
        ".MuiButton-root.bulk-action-flat:hover:not(.Mui-disabled), .MuiButton-root.bulk-action-flat.Mui-focusVisible:not(.Mui-disabled), .MuiButton-root.bulk-action-flat:focus:not(.Mui-disabled), .MuiButton-root.bulk-action-flat:focus-visible:not(.Mui-disabled), .MuiButton-root.bulk-action-flat:active:not(.Mui-disabled)": {
          background: `${SOFT_PURPLE_HOVER_BG} !important`,
          backgroundColor: `${SOFT_PURPLE_HOVER_BG} !important`,
          backgroundImage: "none !important",
          color: `${PURPLE_DARK} !important`,
          border: "none !important",
          borderColor: "transparent !important",
          boxShadow: "none !important",
          outline: "none !important",
        },
        ".MuiButton-root.bulk-action-flat.Mui-disabled": {
          background: "transparent !important",
          backgroundColor: "transparent !important",
          backgroundImage: "none !important",
          border: "none !important",
          borderColor: "transparent !important",
          boxShadow: "none !important",
          outline: "none !important",
        },
        ".MuiTable-root .MuiTableCell-root": {
          fontWeight: "400 !important",
        },
        ".MuiTable-root .MuiTableCell-root *": {
          fontWeight: "400 !important",
        },
        [tableOutlinedPillSelector]: {
          background: "transparent !important",
          border: "1px solid currentColor !important",
          borderRadius: "999px !important",
          boxShadow: "none !important",
          height: "24px !important",
          padding: "0 !important",
          fontSize: "11px !important",
          lineHeight: "20px !important",
          "& .MuiChip-label": {
            padding: "0 10px !important",
          },
        },
        ".MuiTable-root .MuiTableSortLabel-root": {
          fontWeight: "400 !important",
        },
      },
    },

    // Outlined TextField
    MuiTextField: {
      styleOverrides: {
        root: {
          "&.customOutlined": {
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              padding: 0,
              "& fieldset": {
                borderColor: "transparent",
              },
              "&:hover fieldset": {
                borderColor: "white",
              },
              "&.Mui-focused fieldset": {
                borderColor: "white",
              },
            },
            "& input": {
              padding: "14px 22px",
              color: "white",
            },
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        text: {
          color: PILL_BUTTON_BG,
          backgroundColor: "transparent",
          border: "none",
          boxShadow: "none",
          "&:hover": {
            color: PILL_BUTTON_BG,
            backgroundColor: "transparent",
            border: "none",
            boxShadow: "none",
          },
        },
        root: {
          textTransform: "none",
          boxShadow: "none",
          height: `${GLOBAL_BUTTON_HEIGHT}px !important`,
          minHeight: `${GLOBAL_BUTTON_HEIGHT}px !important`,
          paddingTop: "0 !important",
          paddingBottom: "0 !important",
          "& .MuiButton-startIcon > *, & .MuiButton-endIcon > *": {
            width: `${GLOBAL_BUTTON_ICON_SIZE}px !important`,
            height: `${GLOBAL_BUTTON_ICON_SIZE}px !important`,
            minWidth: `${GLOBAL_BUTTON_ICON_SIZE}px`,
            minHeight: `${GLOBAL_BUTTON_ICON_SIZE}px`,
            fontSize: `${GLOBAL_BUTTON_ICON_SIZE}px !important`,
            flexShrink: 0,
          },
          "& .MuiButton-startIcon svg, & .MuiButton-endIcon svg": {
            width: `${GLOBAL_BUTTON_ICON_SIZE}px !important`,
            height: `${GLOBAL_BUTTON_ICON_SIZE}px !important`,
            fontSize: `${GLOBAL_BUTTON_ICON_SIZE}px !important`,
          },
          "& .MuiButton-startIcon img, & .MuiButton-endIcon img": {
            width: `${GLOBAL_BUTTON_ICON_SIZE}px !important`,
            height: `${GLOBAL_BUTTON_ICON_SIZE}px !important`,
            objectFit: "contain",
          },
          "& .MuiButton-startIcon .MuiCircularProgress-root, & .MuiButton-endIcon .MuiCircularProgress-root":
            {
              width: `${GLOBAL_BUTTON_ICON_SIZE}px !important`,
              height: `${GLOBAL_BUTTON_ICON_SIZE}px !important`,
            },
          "&.customOutlined": {
            border: "1px solid white",
            padding: "14px 22px",
            borderRadius: "8px",
            color: "white",
          },
        },
        contained: {
          borderRadius: "999px",
          color: "#FFFFFF",
          backgroundColor: PILL_BUTTON_BG,
          border: `1px solid ${PILL_BUTTON_BG}`,
          boxShadow: "none",
          fontWeight: 400,
          "&:hover, &:active": {
            color: PILL_BUTTON_BG,
            backgroundColor: "#FFFFFF",
            borderColor: PILL_BUTTON_BG,
            boxShadow: "none",
          },
          "&.Mui-disabled": {
            color: PILL_BUTTON_DISABLED_TEXT,
            backgroundColor: PILL_BUTTON_DISABLED_BG,
            borderColor: PILL_BUTTON_DISABLED_BORDER,
          },
        },
      },
    },

    MuiCheckbox: {
      defaultProps: {
        icon: defaultCheckboxIcon,
        checkedIcon: defaultCheckboxCheckedIcon,
        indeterminateIcon: defaultCheckboxIndeterminateIcon,
      },
    },

    // Tab navigation is a shared platform control. Keep its labels consistent
    // across profile headers, list views, dialogs, and standalone pages.
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: "14px !important",
          fontWeight: "400 !important",
          lineHeight: "24px !important",
        },
      },
    },

    // File input (via InputBase)
    MuiInputBase: {
      styleOverrides: {
        // Keep multiline fields visually consistent even when a page provides
        // its own TextField styling.
        inputMultiline: {
          padding: "10px !important",
        },
        root: {
          "&.customFile": {
            border: "1px solid white",
            padding: "14px 22px",
            borderRadius: "8px",
            color: "white",
            "& input": {
              padding: 0,
            },
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          fontWeight: 400,
        },
        head: {
          color: "#344054",
          fontWeight: 400,
          fontSize: "12px !important",
          lineHeight: "20px",
          whiteSpace: "nowrap !important",
        },
        body: {
          fontWeight: 400,
          fontSize: 14,
          lineHeight: "20px",
        },
      },
    },

    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          fontSize: "12px !important",
          whiteSpace: "nowrap !important",
        },
      },
    },
  },
});

export default theme;
